/**
 * monetization/types — model for the decoupled monetization kit (canon).
 *
 * Portfolio's DEFAULT BUSINESS MODEL: the `free` tier is AD-SUPPORTED
 * (monetized with free users from day 1, `adsEnabled: true`) with
 * generous-but-limited quotas, and paid tiers are ad-free with more
 * storage/quotas. Everything is configurable via env (defaults) + admin
 * UI (persisted overrides) without touching code.
 *
 * TypeScript mirror of the Rust model (`core-rust/src/models/monetization.rs` +
 * `monetization/resolve.rs`): keep BOTH in sync.
 */

/** Canonical quota keys, shared across the whole portfolio. */
export const QUOTA_KEYS = ["storage_bytes", "items", "collections", "orgs", "attachments"] as const;

export type QuotaKey = (typeof QUOTA_KEYS)[number];

/** A tier's limits. `null` = unlimited (env: -1). */
export type QuotaLimits = Record<QuotaKey, number | null>;

export interface TierDef {
  /** Stable id ("free", "pro", …). */
  id: string;
  /** Displayable name. */
  label: string;
  /** true ⇒ tier financed with ads (free ad-supported). */
  adsEnabled: boolean;
  limits: QuotaLimits;
}

/** Resolved configuration (code defaults → env → UI overrides). */
export interface MonetizationConfig {
  /** Master switch; false (default) ⇒ the whole layer is inert. */
  enabled: boolean;
  paymentsEnabled: boolean;
  /** Id of the injected PaymentProvider ("none" | "stripe" | …). */
  provider: string;
  /** Tier for those without a subscription — and for guests (guest-first). */
  defaultTier: string;
  tiers: TierDef[];
}

/** A persisted override (row of `monetization_config`, edited in the admin UI). */
export interface MonetizationOverrideRow {
  key: string;
  value: unknown;
}

/** Real usage measured server-side, isolated per user (CC-40). */
export type UsageSnapshot = Record<QuotaKey, number>;

/** Evaluation of a quota for the UI (usage vs limit bar). */
export interface QuotaEvaluation {
  key: QuotaKey;
  used: number;
  /** null = unlimited. */
  limit: number | null;
  /** 0..1 (0 if unlimited). */
  ratio: number;
  /** true from 80% of the limit onward. */
  nearLimit: boolean;
  exceeded: boolean;
}
